import { useCallback, useEffect, useRef, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { useBusiness } from '../context/BusinessContext';
import { api } from '../api/client';
import { processLogo } from '../lib/image';

export default function BusinessProfile() {
  const { activeId, activeBusiness } = useBusiness();
  const fileRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [pNotice, setPNotice] = useState(null);
  const [pError, setPError] = useState(null);

  const toForm = (p) => ({
    legal_name: p?.legal_name || '',
    phone: p?.phone || '',
    email: p?.email || '',
    website: p?.website || '',
    tax_id: p?.tax_id || '',
    address: {
      line1: p?.address?.line1 || '',
      line2: p?.address?.line2 || '',
      city: p?.address?.city || '',
      state: p?.address?.state || '',
      postal_code: p?.address?.postal_code || '',
    },
  });

  const load = useCallback(() => {
    setLoading(true); setNotice(null); setError(null);
    api(`/businesses/${activeId}/profile`)
      .then((p) => { setProfile(p); setForm(toForm(p)); })
      .catch(() => setError('Could not load this business profile.'))
      .finally(() => setLoading(false));
  }, [activeId]);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));

  async function saveProfile() {
    setSaving(true); setPNotice(null); setPError(null);
    try {
      const p = await api(`/businesses/${activeId}/profile`, { method: 'PUT', body: form });
      setProfile(p); setForm(toForm(p));
      setPNotice('Letterhead details saved.');
    } catch (e2) {
      setPError(e2?.detail || 'Could not save the letterhead details.');
    } finally { setSaving(false); }
  }

  async function onPick(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true); setNotice(null); setError(null);
    try {
      const logo_url = await processLogo(file, 512);
      const r = await api(`/businesses/${activeId}/logo`, { method: 'PUT', body: { logo_url } });
      setProfile((p) => ({ ...(p || {}), logo_url: r.logo_url }));
      setNotice('Logo updated. It will appear on this business’s invoice PDFs.');
    } catch (e2) {
      setError(e2?.detail || e2?.message || 'Could not upload that logo.');
    } finally { setBusy(false); }
  }

  async function removeLogo() {
    setBusy(true); setNotice(null); setError(null);
    try {
      const r = await api(`/businesses/${activeId}/logo`, { method: 'PUT', body: { logo_url: null } });
      setProfile((p) => ({ ...(p || {}), logo_url: r.logo_url }));
      setNotice('Logo removed.');
    } catch (e2) {
      setError(e2?.detail || 'Could not remove the logo.');
    } finally { setBusy(false); }
  }

  const logo = profile?.logo_url;

  return (
    <>
      <PageHeader title="Business profile" subtitle={activeBusiness?.name} />
      <section className="panel panel--pad" style={{ maxWidth: 640 }}>
        <h2 className="form-section" style={{ marginTop: 0 }}>Logo</h2>
        <p className="hint" style={{ marginTop: 0 }}>
          Appears on this business’s invoice PDFs. A PNG with a transparent background works best; it’s scaled to fit, so a wide/landscape mark looks cleanest.
        </p>
        {loading ? (
          <p className="hint">Loading…</p>
        ) : (
          <>
            <div className="logo-uploader">
              <div className="logo-preview">
                {logo ? <img src={logo} alt="Business logo" /> : <span className="logo-preview__empty">No logo yet</span>}
              </div>
              <div className="logo-actions">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={onPick} />
                <button className="btn btn--primary" onClick={() => fileRef.current && fileRef.current.click()} disabled={busy}>
                  {busy ? 'Working…' : logo ? 'Replace logo' : 'Upload logo'}
                </button>
                {logo && <button className="btn" onClick={removeLogo} disabled={busy}>Remove</button>}
              </div>
            </div>
            {notice && <div className="form-note" style={{ marginTop: 14 }}>{notice}</div>}
            {error && <div className="form-error" style={{ marginTop: 14 }}>{error}</div>}
          </>
        )}
      </section>

      {!loading && form && (
        <section className="panel panel--pad" style={{ maxWidth: 640 }}>
          <h2 className="form-section" style={{ marginTop: 0 }}>Contact &amp; letterhead</h2>
          <p className="hint" style={{ marginTop: 0 }}>
            Printed on invoices and other correspondence for {activeBusiness?.name || 'this business'}. Leave a field blank to omit it.
          </p>

          <div className="field">
            <label>Legal name</label>
            <input value={form.legal_name} onChange={(e) => setField('legal_name', e.target.value)} placeholder={activeBusiness?.name || ''} />
          </div>

          <div className="field">
            <label>Address line 1</label>
            <input value={form.address.line1} onChange={(e) => setAddr('line1', e.target.value)} />
          </div>
          <div className="field">
            <label>Address line 2</label>
            <input value={form.address.line2} onChange={(e) => setAddr('line2', e.target.value)} placeholder="Suite, unit, etc." />
          </div>
          <div className="form-grid">
            <div className="field"><label>City</label><input value={form.address.city} onChange={(e) => setAddr('city', e.target.value)} /></div>
            <div className="field"><label>State</label><input value={form.address.state} onChange={(e) => setAddr('state', e.target.value)} /></div>
            <div className="field"><label>Postal code</label><input value={form.address.postal_code} onChange={(e) => setAddr('postal_code', e.target.value)} /></div>
          </div>

          <div className="form-grid">
            <div className="field"><label>Phone</label><input value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="(908) 555-1234" /></div>
            <div className="field"><label>Email</label><input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="info@example.com" /></div>
          </div>
          <div className="form-grid">
            <div className="field"><label>Website</label><input value={form.website} onChange={(e) => setField('website', e.target.value)} placeholder="example.com" /></div>
            <div className="field"><label>Tax ID</label><input value={form.tax_id} onChange={(e) => setField('tax_id', e.target.value)} /></div>
          </div>

          <div style={{ marginTop: 16 }}>
            <button className="btn btn--primary" onClick={saveProfile} disabled={saving}>{saving ? 'Saving…' : 'Save details'}</button>
          </div>
          {pNotice && <div className="form-note" style={{ marginTop: 14 }}>{pNotice}</div>}
          {pError && <div className="form-error" style={{ marginTop: 14 }}>{pError}</div>}
        </section>
      )}
    </>
  );
}
